import React, { Component } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

class DockerComposePanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      runsetupId: '',
      fileName: '',
      tasks: [],
      files: [],
      taskDetails: null,
      uploading: false,
      creating: false,
      updating: false,
      runsetupData: {
        name: '',
        template_vm_id: '',
        deployment_path: '',
        ssh_user: '',
        ssh_host: '',
        ssh_key_path: '',
        created_by: 'admin', // Default value, could be fetched from user context
      },
      showCreateForm: false,
      showUpdateForm: false,
      urlResult: '',
      runsetups: [], // Store fetched RunSetups for dropdown
    };
    this.pollInterval = null;
    this.taskListInterval = null;
    this.API_BASE = '/ronin';
    this.filesContainer = null;
  }

  // Normalize various ID shapes (string, number, MongoOID object) to a string
  normalizeId(id) {
    if (id === null || id === undefined) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'number') return String(id);
    if (typeof id === 'object') {
      // MongoDB drivers often return objects containing $oid or oid
      if (id.$oid) return id.$oid;
      if (id.oid) return id.oid;
      if (id['$oid']) return id['$oid'];
      try { return JSON.stringify(id); } catch (e) { return String(id); }
    }
    return String(id);
  }

  componentDidMount() {
    this.listTasks();
    
    // Check sessionStorage for last used RunSetup ID
    const lastRunsetupId = sessionStorage.getItem('lastRunsetupId');
    if (lastRunsetupId) {
      console.log('Component mount: Found RunSetup ID in sessionStorage:', lastRunsetupId);
      this.setState({ runsetupId: lastRunsetupId }, () => {
        // List files for this RunSetup ID - don't show toast for automatic initialization
        this.listFiles(false);
      });
    }
    
    // Then fetch RunSetups from backend to get a default if needed
    this.fetchRunsetups();
    
    // Start periodic task list refresh (every 10 seconds)
    this.taskListInterval = setInterval(() => {
      this.listTasks();
    }, 10000);
  }

  componentDidUpdate(prevProps, prevState) {
    // Save RunSetup ID to sessionStorage whenever it changes
    if (this.state.runsetupId !== prevState.runsetupId) {
      const { runsetupId } = this.state;
      if (runsetupId && runsetupId.trim()) {
        sessionStorage.setItem('lastRunsetupId', runsetupId.trim());
        console.log('Component update: Saved RunSetup ID to sessionStorage:', runsetupId.trim());
      }
    }
  }

  componentWillUnmount() {
    if (this.pollInterval) clearInterval(this.pollInterval);
    if (this.taskListInterval) clearInterval(this.taskListInterval);
  }

  getToken() {
    try {
      const root = JSON.parse(localStorage.getItem('root'));
      const userData = root && JSON.parse(root.userData);
      return (userData && userData.token) || null;
    } catch (e) {
      return null;
    }
  }

  async uploadCompose() {
    const fileInput = this.fileInput;
    const { runsetupId, fileName } = this.state;
    if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
      return toast.error('Please select a file to upload', {
        position: toast.POSITION.TOP_RIGHT
      });
    }
    
    const fd = new FormData();
    fd.append('file', fileInput.files[0]);
    
    // Only append runsetup_id if user has selected one
    // If empty, backend should create a new RunSetup (though this may fail based on previous errors)
    if (runsetupId && runsetupId.trim()) {
      fd.append('runsetup_id', runsetupId.trim());
    }
    // Note: If runsetup_id field is not appended at all, backend may error with "field is required"
    // If empty string is appended, backend may error with "invalid format"
    // We'll let the backend error and show it to the user
    
    fd.append('file_name', fileName || fileInput.files[0].name);
    
    this.setState({ uploading: true });
    const headers = {};
    const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const resp = await fetch(`${this.API_BASE}/run-setup/upload-docker-compose/`, { method: 'POST', body: fd, headers });
      
      if (resp.ok) {
        const result = await resp.json();
        const message = result.message || 'Upload successful';
        toast.success(`Uploaded: ${message}`, {
          position: toast.POSITION.TOP_RIGHT
        });
        
        // Extract RunSetup API ID from response if available and normalize
        let newRunsetupApiId = null;
        if (result.runsetup_id) newRunsetupApiId = this.normalizeId(result.runsetup_id);

        // If backend returned an API id, refresh runsetups and select the corresponding normalized UI id
        if (newRunsetupApiId) {
          try { await this.fetchRunsetups(); } catch (e) { /* ignore */ }
          const found = (this.state.runsetups || []).find(rs => rs._api_id === newRunsetupApiId);
          if (found) {
            this.setState({ runsetupId: found._id_norm });
            this.listFiles(true);
          } else {
            // Fallback: set raw api id so manual input can use it
            this.setState({ runsetupId: newRunsetupApiId }, () => this.listFiles(true));
          }
        } else {
          // No API id returned; if user had selected one already, just refresh files
          if (runsetupId && runsetupId.trim()) {
            this.listFiles(true);
          }
        }
      } else {
        const text = await resp.text();
        toast.error(`Upload failed: ${text}`, {
          position: toast.POSITION.TOP_RIGHT
        });
      }
      
      this.listTasks();
    } catch (err) {
      toast.error(`Upload failed: ${err}`, {
        position: toast.POSITION.TOP_RIGHT
      });
    } finally {
      this.setState({ uploading: false });
    }
  }

  async executeRunSetup(cmd) {
    const { runsetupId, runsetups } = this.state;
    const idCandidate = runsetupId ? runsetupId.trim() : '';
    // Resolve API id if the selected item is synthetic
    const selected = (runsetups || []).find(rs => rs._id_norm === idCandidate || rs._api_id === idCandidate);
    const apiId = selected ? selected._api_id : idCandidate;
    if (!apiId) {
      return toast.error('Provide a valid RunSetup ID (selected item has no API id)', {
        position: toast.POSITION.TOP_RIGHT
      });
    }
    const headers = { 'Content-Type': 'application/json' };
    const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
    const path = cmd;
    try {
      const resp = await fetch(`${this.API_BASE}/run-setup/${encodeURIComponent(apiId)}/${path}/`, { method: 'POST', headers });
      const text = await resp.text();
      
      // Check if the response indicates success
      if (resp.ok) {
        toast.success(`Docker Compose '${cmd}' started as background task`, {
          position: toast.POSITION.TOP_RIGHT
        });
      } else {
        toast.error(`Failed to execute ${cmd}: ${text}`, {
          position: toast.POSITION.TOP_RIGHT
        });
      }
      this.listTasks();
    } catch (err) {
      toast.error(`Request failed: ${err}`, {
        position: toast.POSITION.TOP_RIGHT
      });
    }
  }

  async getRunSetupUrl() {
    const { runsetupId, runsetups } = this.state;
    const idCandidate = runsetupId ? runsetupId.trim() : '';
    const selected = (runsetups || []).find(rs => rs._id_norm === idCandidate || rs._api_id === idCandidate);
    const apiId = selected ? selected._api_id : idCandidate;
    if (!apiId) {
      toast.error('Provide a valid RunSetup ID (selected item has no API id)', {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }
    const headers = {};
    const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const resp = await fetch(`${this.API_BASE}/run-setup/${encodeURIComponent(apiId)}/url/`, { headers });
      const text = await resp.text();
      
      // Try to parse as JSON
      try {
        const data = JSON.parse(text);
        
        // Check if we have the new enhanced response format
        let htmlContent = '';
        if (data.clickable_links) {
          // Use the pre-formatted HTML links from backend
          htmlContent = data.clickable_links;
        } else if (data.html_links && Array.isArray(data.html_links)) {
          // Join HTML links with line breaks
          htmlContent = data.html_links.join('<br/>');
        } else if (data.urls && Array.isArray(data.urls)) {
          // Old format: create links from URLs array
          htmlContent = data.urls.map((url, index) =>
            `<a href="${url}" target="_blank" style="color: #f18f3c; text-decoration: none;">${url}</a>`
          ).join('<br/>');
        } else if (data.message) {
          // Just show the message if no links
          htmlContent = data.message;
        } else {
          // Fallback to raw text (escape HTML)
          htmlContent = text.replace(/</g, '<').replace(/>/g, '>');
        }
        
        this.setState({ urlResult: htmlContent });
        toast.success('URLs fetched successfully', {
          position: toast.POSITION.TOP_RIGHT
        });
      } catch (parseError) {
        // If not JSON, display as plain text (escape HTML)
        const escapedText = text.replace(/</g, '<').replace(/>/g, '>');
        this.setState({ urlResult: escapedText });
        toast.success('URL fetched successfully', {
          position: toast.POSITION.TOP_RIGHT
        });
      }
    } catch (err) {
      toast.error(`Request failed: ${err}`, {
        position: toast.POSITION.TOP_RIGHT
      });
    }
  }

  async createRunSetup() {
    const { runsetupData } = this.state;
    if (!runsetupData.name || !runsetupData.deployment_path) {
      return toast.error('Name and Deployment Path are required', {
        position: toast.POSITION.TOP_RIGHT
      });
    }
    
    // Construct credentials_ref from ssh_user and ssh_host
    let credentials_ref = '';
    if (runsetupData.ssh_user && runsetupData.ssh_host) {
      credentials_ref = `${runsetupData.ssh_user}@${runsetupData.ssh_host}`;
    }
    
    // Prepare the data for backend
    const backendData = {
      name: runsetupData.name,
      template_vm_id: runsetupData.template_vm_id || '', // Required field
      deployment_path: runsetupData.deployment_path,
      credentials_ref: credentials_ref,
      scripts: [], // Optional, can be empty array
      created_by: runsetupData.created_by || 'admin',
    };
    
    this.setState({ creating: true });

    const headers = { 'Content-Type': 'application/json' };
    const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const resp = await fetch(`${this.API_BASE}/run-setup/create/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(backendData)
      });

      // Prefer JSON response but fall back to text
      let body = null;
      try {
        body = await resp.json();
      } catch (e) {
        body = await resp.text();
      }

      if (resp.ok) {
        // Try to extract the new ID from common fields
        let newId = null;
        if (body && typeof body === 'object') {
          newId = body.insertedId || body._id || body.id || body.inserted_id || null;
        } else if (typeof body === 'string') {
          newId = body;
        }

        // Normalize the new ID to string form
        let newIdNorm = this.normalizeId(newId);

        // Capture creation keys to attempt a fallback match if API didn't return an ID
        const createdName = runsetupData.name;
        const createdPath = runsetupData.deployment_path;

        // Refresh runsetups and select the new one if we have an ID
        try { await this.fetchRunsetups(); } catch (e) { /* ignore */ }

        // If API didn't return an ID, try to find the created item by name+path
        if (!newIdNorm && createdName) {
          const found = (this.state.runsetups || []).find(rs => (rs.name === createdName && (rs.deployment_path === createdPath || !createdPath)));
          if (found && found._id_norm) newIdNorm = found._id_norm;
        }

        this.setState({
          creating: false,
          showCreateForm: false,
          runsetupData: {
            name: '',
            template_vm_id: '',
            deployment_path: '',
            ssh_user: '',
            ssh_host: '',
            ssh_key_path: '',
            created_by: 'admin',
          },
          runsetupId: newIdNorm
        }, () => {
          if (newIdNorm) {
            toast.success(`Created RunSetup: ${newIdNorm}`, { position: toast.POSITION.TOP_RIGHT });
            this.listFiles(true);
          } else {
            toast.success('Created RunSetup', { position: toast.POSITION.TOP_RIGHT });
          }
        });
      } else {
        const text = (body && typeof body === 'string') ? body : (body ? JSON.stringify(body) : 'Unknown error');
        toast.error(`Error: ${text}`, { position: toast.POSITION.TOP_RIGHT });
        this.setState({ creating: false });
      }
    } catch (err) {
      toast.error(`Create failed: ${err}`, { position: toast.POSITION.TOP_RIGHT });
      this.setState({ creating: false });
    }
  }

  async updateRunSetup() {
    const { runsetupId, runsetups, runsetupData } = this.state;
    const idCandidate = runsetupId ? runsetupId.trim() : '';
    const selected = (runsetups || []).find(rs => rs._id_norm === idCandidate || rs._api_id === idCandidate);
    const apiId = selected ? selected._api_id : idCandidate;
    if (!apiId) {
      return toast.error('Provide a RunSetup ID to update (selected item has no API id)', {
        position: toast.POSITION.TOP_RIGHT
      });
    }
    
    // Construct credentials_ref from ssh_user and ssh_host
    let credentials_ref = '';
    if (runsetupData.ssh_user && runsetupData.ssh_host) {
      credentials_ref = `${runsetupData.ssh_user}@${runsetupData.ssh_host}`;
    }
    
    // Prepare the data for backend
    const backendData = {
      name: runsetupData.name,
      template_vm_id: runsetupData.template_vm_id || '',
      deployment_path: runsetupData.deployment_path,
      credentials_ref: credentials_ref,
      scripts: [], // Optional, can be empty array
      created_by: runsetupData.created_by || 'admin',
    };
    
    this.setState({ updating: true });

    const headers = { 'Content-Type': 'application/json' };
    const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const resp = await fetch(`${this.API_BASE}/run-setup/update/${encodeURIComponent(apiId)}/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(backendData)
      });
      const text = await resp.text();
      if (resp.ok) {
        toast.success(`Updated: ${text}`, {
          position: toast.POSITION.TOP_RIGHT
        });
        this.setState({
          updating: false,
          showUpdateForm: false
        });
      } else {
        toast.error(`Error: ${text}`, {
          position: toast.POSITION.TOP_RIGHT
        });
        this.setState({ updating: false });
      }
    } catch (err) {
      toast.error(`Update failed: ${err}`, {
        position: toast.POSITION.TOP_RIGHT
      });
      this.setState({ updating: false });
    }
  }

  handleRunSetupDataChange(field, value) {
    this.setState(prevState => ({
      runsetupData: {
        ...prevState.runsetupData,
        [field]: value
      }
    }));
  }

  async listTasks() {
    const headers = {};
    const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const resp = await fetch(`${this.API_BASE}/docker-compose/tasks/`, { headers });
      const data = await resp.json();
      // Backend returns { "tasks": [...], "total": N }
      const tasks = data.tasks || [];
      this.setState({ tasks: Array.isArray(tasks) ? tasks : [] });
    } catch (err) {
      this.setState({ tasks: [] });
    }
  }

  async fetchRunsetups() {
    const headers = {};
    const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const resp = await fetch(`${this.API_BASE}/run-setup/list/`, { headers });
      if (resp.ok) {
        const runsetups = await resp.json();
        console.log('Fetched RunSetups:', runsetups);
        
        // Normalize RunSetups and store in state for dropdown
              const normalized = Array.isArray(runsetups) ? runsetups.map((rs, idx) => {
                // Determine the real API id if present
                const apiIdCandidate = rs._id || rs.id || rs.runsetup_id || rs.insertedId || rs.inserted_id;
                const apiId = this.normalizeId(apiIdCandidate);
                // If no real id, create a stable synthetic id for UI selection
                const syntheticId = `synthetic-${idx}-${this.normalizeId(rs.name || rs.deployment_path || String(idx))}`;
                const idNorm = apiId || syntheticId;
                return {
                  ...rs,
                  _api_id: apiId || null,
                  _id_norm: idNorm,
                  _synthetic: !apiId
                };
              }) : [];
        this.setState({ runsetups: normalized });
        
        // Try to get RunSetup ID from sessionStorage first (last used)
        const lastRunsetupId = sessionStorage.getItem('lastRunsetupId');
        
        // Only set a default RunSetup ID if we have one in sessionStorage
        // Otherwise leave it empty as per user request
        if (lastRunsetupId) {
          // Check if the last used ID exists in the fetched RunSetups
          const lastRunsetupExists = Array.isArray(runsetups) && normalized.some(rs => rs._id_norm === lastRunsetupId || rs._api_id === lastRunsetupId);
          
          if (lastRunsetupExists) {
            console.log('Using last RunSetup ID from sessionStorage:', lastRunsetupId);
            this.setState({ runsetupId: lastRunsetupId }, () => {
              // List files for this RunSetup ID
              this.listFiles(false);
            });
          } else {
            // Last used ID no longer exists, clear it
            console.log('Last used RunSetup ID no longer exists:', lastRunsetupId);
            sessionStorage.removeItem('lastRunsetupId');
            this.setState({ runsetupId: '' });
          }
        } else {
          // No last used ID, leave empty as default
          this.setState({ runsetupId: '' });
        }
      } else {
        const errorText = await resp.text();
        console.error('Failed to fetch RunSetups:', errorText);
        // Don't show error to user, just log it
      }
    } catch (err) {
      console.error('Failed to fetch RunSetups:', err);
      // Don't show error to user, just log it
    }
  }

  async refreshRunsetups() {
    await this.fetchRunsetups();
    toast.success('RunSetups list refreshed', {
      position: toast.POSITION.TOP_RIGHT
    });
  }

  async listFiles(showSuccessToast = false) {
    const { runsetupId, runsetups } = this.state;
    if (!runsetupId || !runsetupId.trim()) {
      // If no RunSetup ID is provided, we cannot list files
      this.setState({ files: [] });
      return;
    }

    // Resolve API id from selected runsetup (handles synthetic ids)
    const selected = (runsetups || []).find(rs => rs._id_norm === runsetupId || rs._api_id === runsetupId);
    const apiId = selected ? selected._api_id : runsetupId;
    if (!apiId) {
      // No actual API id available for this selection
      toast.error('Selected RunSetup has no API id to fetch files', { position: toast.POSITION.TOP_RIGHT });
      this.setState({ files: [] });
      return;
    }
    const headers = {};
    const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const resp = await fetch(`${this.API_BASE}/run-setup/${encodeURIComponent(String(apiId).trim())}/files/`, { headers });
      if (resp.ok) {
        const files = await resp.json();
        this.setState({ files: Array.isArray(files) ? files : [] }, () => {
          if (showSuccessToast) {
            toast.success('Files list refreshed', {
              position: toast.POSITION.TOP_RIGHT
            });
            if (this.filesContainer && typeof this.filesContainer.scrollIntoView === 'function') {
              try {
                this.filesContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } catch (e) {
                // ignore scrolling errors
              }
            }
          }
        });
      } else {
        const errorText = await resp.text();
        this.setState({
          files: []
        });
        toast.error(`Failed to list files: ${errorText || 'Unknown error'}`, {
          position: toast.POSITION.TOP_RIGHT
        });
      }
    } catch (err) {
      this.setState({
        files: []
      });
      toast.error(`Failed to list files: ${err.message || 'Network error'}`, {
        position: toast.POSITION.TOP_RIGHT
      });
    }
  }

  async showTaskStatus(taskId) {
    if (!taskId) return;
    const headers = {};
    const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const resp = await fetch(`${this.API_BASE}/docker-compose/task/${encodeURIComponent(taskId)}/status/`, { headers });
      const data = await resp.json();
      this.setState({ taskDetails: data });
      
      // Update the task in the tasks array with the latest status
      if (data.status) {
        this.setState(prevState => {
          const updatedTasks = prevState.tasks.map(task => {
            const taskIdToCompare = task.task_id || task.id;
            const dataTaskId = data.task_id || data.id;
            if (taskIdToCompare === dataTaskId || taskIdToCompare === taskId) {
              return { ...task, status: data.status };
            }
            return task;
          });
          return { tasks: updatedTasks };
        });
      }
      
      this.startPolling(taskId);
    } catch (err) {
      this.setState({ taskDetails: { error: String(err) } });
    }
  }

  startPolling(taskId) {
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.pollInterval = setInterval(async () => {
      try {
        const headers = {};
        const token = this.getToken(); if (token) headers['Authorization'] = `Bearer ${token}`;
        const resp = await fetch(`${this.API_BASE}/docker-compose/task/${encodeURIComponent(taskId)}/status/`, { headers });
        const data = await resp.json();
        this.setState({ taskDetails: data });
        
        // Update the task in the tasks array with the latest status
        if (data.status) {
          this.setState(prevState => {
            const updatedTasks = prevState.tasks.map(task => {
              const taskIdToCompare = task.task_id || task.id;
              const dataTaskId = data.task_id || data.id;
              if (taskIdToCompare === dataTaskId || taskIdToCompare === taskId) {
                return { ...task, status: data.status };
              }
              return task;
            });
            return { tasks: updatedTasks };
          });
        }
        
        if (data.status && ['completed','failed','stopped'].includes(String(data.status).toLowerCase())){
          clearInterval(this.pollInterval); this.pollInterval = null;
          // Force a fresh fetch of all tasks to ensure consistency
          this.listTasks();
        }
      } catch (e) {
        // ignore
      }
    }, 5000);
  }

  render() {
        const { runsetupId, fileName, tasks, files, taskDetails, uploading,
          creating, updating, runsetupData, showCreateForm, showUpdateForm, urlResult, runsetups } = this.state;

        // Selected RunSetup details for the right column info panel (compare normalized IDs)
        const selectedRunsetup = runsetups.find(rs => rs._id_norm === this.normalizeId(runsetupId)) || null;

    return (
      <div className="docker-compose-panel" style={{
        padding: '15px',
        marginLeft: '10px',
        marginRight: '10px',
        overflow: 'auto',
        height: 'calc(100vh - 100px)'
      }}>
        {/* CSS Animations for spinner and progress bar */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>
        
        <h3 style={{ marginBottom: '20px', color: '#f18f3c' }}>Docker Compose Management</h3>

        {/* Toast Container for notifications */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />

        {/* Main Grid Layout */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '3fr 1fr', 
          gap: '15px',
          marginBottom: '20px'
        }}>
          
          {/* Upload Section - Enhanced */}
          <div style={{
            padding: '20px',
            border: '1px solid #444',
            borderRadius: '8px',
            backgroundColor: '#1a1a1a',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h4 style={{ color: '#f18f3c', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.2em' }}>📤</span>
                Upload Docker Compose
              </h4>
              <button
                onClick={() => this.refreshRunsetups()}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
                title="Refresh RunSetups list"
              >
                <span>↻</span>
                Refresh
              </button>
            </div>            
            {/* RunSetup ID Section */}
            <div style={{
              padding: '15px',
              backgroundColor: '#2a2a2a',
              borderRadius: '5px',
              marginBottom: '15px',
              borderLeft: '3px solid #f18f3c'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ color: '#ccc', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span>🏷️</span>
                  RunSetup ID (optional)
                </label>
                <span style={{ fontSize: '0.8em', color: '#aaa', backgroundColor: '#3a3a3a', padding: '2px 6px', borderRadius: '3px' }}>
                  {runsetups.length} available
                </span>
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <select
                    value={runsetupId}
                    onChange={(e) => this.setState({ runsetupId: this.normalizeId(e.target.value) })}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: '1px solid #555',
                      borderRadius: '4px',
                      backgroundColor: '#1a1a1a',
                      color: '#fff',
                      minHeight: '40px',
                      fontSize: '0.95em'
                    }}
                  >
                    <option value="">Select a RunSetup...</option>
                    {runsetups.map((rs) => {
                      const id = rs._id_norm;
                      const apiId = rs._api_id;
                      const name = rs.name || 'Unnamed';
                      const deploymentPath = rs.deployment_path || 'No path';
                      const displayId = apiId ? (apiId.substring ? apiId.substring(0, 8) + '...' : String(apiId).substring(0, 8) + '...') : (id ? id.substring(0, 12) + ' (synthetic)' : 'N/A');
                      return (
                        <option key={id} value={id}>
                          {name} ({displayId}) - {deploymentPath}
                        </option>
                      );
                    })}
                  </select>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      type="text"
                      value={runsetupId}
                      onChange={(e) => this.setState({ runsetupId: this.normalizeId(e.target.value) })}
                      placeholder="Or enter ID manually"
                      style={{
                        width: '100%',
                        padding: '10px 10px 10px 35px',
                        border: '1px solid #555',
                        borderRadius: '4px',
                        backgroundColor: '#1a1a1a',
                        color: '#fff',
                        fontSize: '0.95em'
                      }}
                    />
                    <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#aaa' }}>🔍</span>
                  </div>
                </div>
                {runsetupId && runsetupId.trim() && (
                  <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#4CAF50', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span>✓</span>
                    RunSetup ID is set: {runsetupId.substring ? runsetupId.substring(0, 12) + '...' : String(runsetupId).substring(0, 12) + '...'}
                  </div>
                )}
              </div>
              
              <div style={{ fontSize: '0.85em', color: '#aaa', lineHeight: '1.4' }}>
                <p style={{ margin: 0 }}>Select a RunSetup to associate this Docker Compose file with, or leave empty to create a new one.</p>
              </div>
            </div>

            {/* File Details Section */}
            <div style={{
              padding: '15px',
              backgroundColor: '#2a2a2a',
              borderRadius: '5px',
              marginBottom: '15px',
              borderLeft: '3px solid #2196F3'
            }}>
              <label style={{ color: '#ccc', fontWeight: 'bold', display: 'block', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>📝</span>
                File Details
              </label>
              
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                  <label style={{ color: '#ccc', fontSize: '0.9em', flex: 1 }}>Custom file name (optional)</label>
                  <span style={{ fontSize: '0.75em', color: '#aaa', backgroundColor: '#3a3a3a', padding: '2px 6px', borderRadius: '3px' }}>
                    Optional
                  </span>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={fileName}
                    onChange={(e)=>this.setState({fileName:e.target.value})}
                    placeholder="Leave empty to use original filename"
                    style={{
                      width: '100%',
                      padding: '10px 10px 10px 35px',
                      border: '1px solid #555',
                      borderRadius: '4px',
                      backgroundColor: '#1a1a1a',
                      color: '#fff',
                      fontSize: '0.95em'
                    }}
                  />
                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#aaa' }}>📄</span>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                  <label style={{ color: '#ccc', fontSize: '0.9em', flex: 1 }}>Docker Compose file</label>
                  <span style={{ fontSize: '0.75em', color: '#f18f3c', backgroundColor: '#3a3a3a', padding: '2px 6px', borderRadius: '3px' }}>
                    Required
                  </span>
                </div>
                <div style={{
                  border: '2px dashed #555',
                  borderRadius: '5px',
                  padding: '15px',
                  textAlign: 'center',
                  backgroundColor: '#1a1a1a',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => this.fileInput && this.fileInput.click()}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#f18f3c'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#555'}
                >
                  <div style={{ fontSize: '3em', color: '#555', marginBottom: '10px' }}>📎</div>
                  <div style={{ color: '#ccc', marginBottom: '5px' }}>
                    {this.fileInput && this.fileInput.files && this.fileInput.files.length > 0
                      ? `Selected: ${this.fileInput.files[0].name}`
                      : 'Click to select or drag a Docker Compose file'}
                  </div>
                  <div style={{ fontSize: '0.85em', color: '#aaa' }}>
                    Supports .yml, .yaml files
                  </div>
                  <input
                    type="file"
                    ref={el=>this.fileInput = el}
                    style={{ display: 'none' }}
                    accept=".yml,.yaml"
                  />
                </div>
              </div>
            </div>

            {/* Upload Button */}
            <button
              onClick={()=>this.uploadCompose()}
              disabled={uploading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: uploading ? '#555' : '#f18f3c',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontSize: '1em',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                transition: 'all 0.2s',
                boxShadow: uploading ? 'none' : '0 2px 4px rgba(241, 143, 60, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!uploading) e.currentTarget.style.backgroundColor = '#e67e22';
              }}
              onMouseLeave={(e) => {
                if (!uploading) e.currentTarget.style.backgroundColor = '#f18f3c';
              }}
            >
              {uploading ? (
                <>
                  <span className="spinner" style={{ display: 'inline-block', width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                  Uploading...
                </>
              ) : (
                <>
                  <span>🚀</span>
                  Upload Docker Compose File
                </>
              )}
            </button>
            
            {/* Upload Status */}
            {uploading && (
              <div style={{
                marginTop: '15px',
                padding: '10px',
                backgroundColor: '#2a2a2a',
                borderRadius: '5px',
                borderLeft: '3px solid #f18f3c'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <span style={{ color: '#ccc', fontSize: '0.9em' }}>Upload in progress...</span>
                  <span style={{ fontSize: '0.8em', color: '#aaa' }}>Please wait</span>
                </div>
                <div style={{ width: '100%', height: '4px', backgroundColor: '#444', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{
                    width: '60%',
                    height: '100%',
                    backgroundColor: '#f18f3c',
                    animation: 'pulse 1.5s infinite'
                  }}></div>
                </div>
              </div>
            )}
            
            {/* Instructions */}
            <div style={{
              marginTop: '15px',
              padding: '12px',
              backgroundColor: '#2a2a2a',
              borderRadius: '5px',
              fontSize: '0.85em',
              color: '#aaa',
              borderLeft: '3px solid #4CAF50'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px', color: '#ccc' }}>
                <span>💡</span>
                <strong style={{ marginLeft: '5px' }}>How it works:</strong>
              </div>
              <ol style={{ margin: '0', paddingLeft: '20px', lineHeight: '1.5' }}>
                <li>Select a RunSetup ID or leave empty to create a new one</li>
                <li>Choose a Docker Compose file (.yml or .yaml)</li>
                <li>Optionally provide a custom file name</li>
                <li>Click "Upload Docker Compose File" to upload</li>
              </ol>
            </div>
          </div>

          {/* RunSetup Actions - Enhanced & Compact */}
          <div style={{
            padding: '15px',
            border: '1px solid #444',
            borderRadius: '5px',
            backgroundColor: '#1a1a1a',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            alignSelf: 'start'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ color: '#f18f3c', margin: 0, fontSize: '1.1em' }}>RunSetup Actions</h4>
              <button
                onClick={() => this.refreshRunsetups()}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '0.8em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Refresh RunSetups list"
              >
                <span>↻</span>
                Refresh
              </button>
            </div>
            
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <label style={{ color: '#ccc', fontSize: '0.9em' }}>RunSetup ID</label>
                {runsetupId && runsetupId.trim() && (
                  <span style={{
                    fontSize: '0.75em',
                    backgroundColor: '#3a3a3a',
                    padding: '2px 5px',
                    borderRadius: '3px',
                    color: '#aaa'
                  }}>
                    Active
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={runsetupId}
                  onChange={(e) => this.setState({ runsetupId: this.normalizeId(e.target.value) })}
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    backgroundColor: '#2a2a2a',
                    color: '#fff',
                    minHeight: '36px',
                    fontSize: '0.9em'
                  }}
                >
                  <option value="">Select a RunSetup...</option>
                  {runsetups.map((rs) => {
                    const id = rs._id_norm;
                    const apiId = rs._api_id;
                    const name = rs.name || 'Unnamed';
                    const displayId = apiId ? (apiId.substring ? apiId.substring(0, 8) + '...' : String(apiId).substring(0, 8) + '...') : (id ? id.substring(0, 12) + ' (synthetic)' : 'N/A');
                    return (
                      <option key={id} value={id}>
                        {name} ({displayId})
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  value={runsetupId}
                  onChange={(e) => this.setState({ runsetupId: this.normalizeId(e.target.value) })}
                  placeholder="Or enter ID"
                  style={{
                    flex: 1,
                    padding: '8px',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    backgroundColor: '#2a2a2a',
                    color: '#fff',
                    fontSize: '0.9em'
                  }}
                />
              </div>
              {runsetups.length > 0 && (
                <div style={{ marginTop: '4px', fontSize: '0.8em', color: '#aaa' }}>
                  {runsetups.length} RunSetup(s) available
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={()=>this.setState({ showCreateForm: !showCreateForm, showUpdateForm: false })}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: showCreateForm ? '#555' : '#444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px'
                }}
              >
                <span>+</span>
                {showCreateForm ? 'Cancel' : 'Create'}
              </button>
              <button
                onClick={()=>this.setState({ showUpdateForm: !showUpdateForm, showCreateForm: false })}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: showUpdateForm ? '#555' : '#444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '5px'
                }}
              >
                <span>✎</span>
                {showUpdateForm ? 'Cancel' : 'Update'}
              </button>
            </div>

            {/* Create Form */}
            {showCreateForm && (
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#2a2a2a', 
                borderRadius: '3px',
                marginBottom: '10px'
              }}>
                <h5 style={{ marginBottom: '10px', color: '#f18f3c' }}>Create RunSetup</h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="text"
                    value={runsetupData.name}
                    onChange={(e)=>this.handleRunSetupDataChange('name', e.target.value)}
                    placeholder="Name *"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.template_vm_id}
                    onChange={(e)=>this.handleRunSetupDataChange('template_vm_id', e.target.value)}
                    placeholder="Template VM ID *"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.deployment_path}
                    onChange={(e)=>this.handleRunSetupDataChange('deployment_path', e.target.value)}
                    placeholder="Deployment Path *"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.ssh_user}
                    onChange={(e)=>this.handleRunSetupDataChange('ssh_user', e.target.value)}
                    placeholder="SSH User"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.ssh_host}
                    onChange={(e)=>this.handleRunSetupDataChange('ssh_host', e.target.value)}
                    placeholder="SSH Host"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.ssh_key_path}
                    onChange={(e)=>this.handleRunSetupDataChange('ssh_key_path', e.target.value)}
                    placeholder="SSH Key Path"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                </div>
                <button
                  onClick={()=>this.createRunSetup()}
                  disabled={creating}
                  style={{
                    marginTop: '10px',
                    padding: '8px',
                    backgroundColor: '#f18f3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: creating ? 'not-allowed' : 'pointer',
                    width: '100%'
                  }}
                >
                  {creating ? 'Creating...' : 'Create RunSetup'}
                </button>
              </div>
            )}

            {/* Update Form */}
            {showUpdateForm && (
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#2a2a2a', 
                borderRadius: '3px',
                marginBottom: '10px'
              }}>
                <h5 style={{ marginBottom: '10px', color: '#f18f3c' }}>Update RunSetup</h5>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input
                    type="text"
                    value={runsetupData.name}
                    onChange={(e)=>this.handleRunSetupDataChange('name', e.target.value)}
                    placeholder="Name"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.template_vm_id}
                    onChange={(e)=>this.handleRunSetupDataChange('template_vm_id', e.target.value)}
                    placeholder="Template VM ID"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.deployment_path}
                    onChange={(e)=>this.handleRunSetupDataChange('deployment_path', e.target.value)}
                    placeholder="Deployment Path"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.ssh_user}
                    onChange={(e)=>this.handleRunSetupDataChange('ssh_user', e.target.value)}
                    placeholder="SSH User"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.ssh_host}
                    onChange={(e)=>this.handleRunSetupDataChange('ssh_host', e.target.value)}
                    placeholder="SSH Host"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                  <input
                    type="text"
                    value={runsetupData.ssh_key_path}
                    onChange={(e)=>this.handleRunSetupDataChange('ssh_key_path', e.target.value)}
                    placeholder="SSH Key Path"
                    style={{ padding: '6px', border: '1px solid #555', borderRadius: '2px', backgroundColor: '#1a1a1a', color: '#fff' }}
                  />
                </div>
                <button
                  onClick={()=>this.updateRunSetup()}
                  disabled={updating}
                  style={{
                    marginTop: '10px',
                    padding: '8px',
                    backgroundColor: '#f18f3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: updating ? 'not-allowed' : 'pointer',
                    width: '100%'
                  }}
                >
                  {updating ? 'Updating...' : 'Update RunSetup'}
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={()=>this.executeRunSetup('start')}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <span>▶</span>
                Start
              </button>
              <button
                onClick={()=>this.executeRunSetup('stop')}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <span>⏹</span>
                Stop
              </button>
              <button
                onClick={()=>this.getRunSetupUrl()}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <span>🔗</span>
                Get URL
              </button>
            </div>
            {/* Recent Tasks - fill remaining space: moved directly under actions */}
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#2a2a2a',
              borderRadius: '4px',
              color: '#ccc'
            }}>
              <h5 style={{ margin: '0 0 8px 0', color: '#f18f3c' }}>Recent Tasks</h5>
              {tasks && tasks.length > 0 ? (
                <div style={{ fontSize: '0.9em', lineHeight: '1.6' }}>
                  {tasks.slice(0, 6).map((t, idx) => {
                    const id = t.task_id || t.id || idx;
                    return (
                      <div key={id} style={{ padding: '8px', backgroundColor: '#1f1f1f', borderRadius: '3px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                          <strong style={{ color: '#f18f3c' }}>{t.command || t.action || 'task'}</strong>
                          <div style={{ color: '#aaa', fontSize: '0.85em' }}>{id}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <div style={{ fontSize: '0.85em', color: '#9e9e9e' }}>{t.status || 'unknown'}</div>
                          <button
                            onClick={() => { this.showTaskStatus(t.task_id || t.id); this.startPolling(t.task_id || t.id); }}
                            style={{ padding: '6px 8px', backgroundColor: '#444', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.85em' }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: '0.9em', color: '#aaa' }}>No recent tasks</div>
              )}
            </div>

            {/* URL Result */}
            {urlResult && (
              <div style={{
                marginTop: '10px',
                padding: '8px',
                backgroundColor: '#2a2a2a',
                borderRadius: '3px',
                color: '#ccc'
              }}>
                <div dangerouslySetInnerHTML={{ __html: urlResult }} />
              </div>
            )}

            {/* RunSetup Info - uses otherwise-empty space */}
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#2a2a2a',
              borderRadius: '4px',
              color: '#ccc'
            }}>
              <h5 style={{ margin: '0 0 8px 0', color: '#f18f3c' }}>RunSetup Info</h5>
              {selectedRunsetup ? (
                <div style={{ fontSize: '0.9em', lineHeight: '1.5' }}>
                  <div><strong>Name:</strong> {selectedRunsetup.name || '—'}</div>
                  <div><strong>Deployment Path:</strong> {selectedRunsetup.deployment_path || '—'}</div>
                  <div><strong>Credentials:</strong> {selectedRunsetup.credentials_ref || '—'}</div>
                  <div><strong>Scripts:</strong> {Array.isArray(selectedRunsetup.scripts) ? selectedRunsetup.scripts.length : '—'}</div>
                  <div style={{ marginTop: '8px' }}>
                    <button
                      onClick={() => { this.setState({ runsetupId: selectedRunsetup._id_norm }, () => this.listFiles(true)); }}
                      style={{ padding: '6px 10px', backgroundColor: '#444', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.85em' }}
                    >
                      View Files
                    </button>
                    <button
                      onClick={() => this.getRunSetupUrl()}
                      style={{ padding: '6px 10px', marginLeft: '8px', backgroundColor: '#17a2b8', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.85em' }}
                    >
                      Get URL
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '0.9em', color: '#aaa' }}>
                  No RunSetup selected. Select one above to view quick details here.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tasks Section */}
        <div style={{
          padding: '15px',
          border: '1px solid #444',
          borderRadius: '5px',
          backgroundColor: '#1a1a1a'
        }}>
          <h4 style={{ marginBottom: '15px', color: '#f18f3c' }}>Docker Compose Tasks</h4>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {tasks.length === 0 && <p style={{ color: '#ccc' }}>No tasks available.</p>}
            {tasks.map(task => (
              <div
                key={task.task_id || task.id}
                onClick={() => this.showTaskStatus(task.task_id || task.id)}
                style={{
                  padding: '10px',
                  borderBottom: '1px solid #333',
                  cursor: 'pointer',
                  backgroundColor: taskDetails && (taskDetails.task_id === task.task_id || taskDetails.id === task.id) ? '#333' : 'transparent'
                }}
              >
                <strong style={{ color: '#f18f3c' }}>Task ID:</strong> {task.task_id || task.id} &nbsp; | &nbsp;
                <strong style={{ color: '#f18f3c' }}>Command:</strong> {task.command || 'N/A'} &nbsp; | &nbsp;
                <strong style={{ color: '#f18f3c' }}>Status:</strong> {task.status || 'unknown'}
              </div>
            ))}
          </div>

          {/* Task Details */}
          {taskDetails && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: '#2a2a2a',
              borderRadius: '5px'
            }}>
              <h5 style={{ marginBottom: '10px', color: '#f18f3c' }}>Task Details (ID: {taskDetails.id})</h5>
              <pre style={{ whiteSpace: 'pre-wrap', color: '#ccc' }}>
                {JSON.stringify(taskDetails, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Uploaded Files Section */}
        <div style={{
          padding: '15px',
          border: '1px solid #444',
          borderRadius: '5px',
          backgroundColor: '#1a1a1a',
          marginTop: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h4 style={{ color: '#f18f3c', margin: 0 }}>Uploaded Docker Compose Files</h4>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => this.refreshRunsetups()}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
                title="Refresh RunSetups list"
              >
                Refresh RunSetups
              </button>
              <button
                onClick={() => this.listFiles(true)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                Refresh Files
              </button>
            </div>
          </div>
          
          {/* RunSetups List - Always shown when available */}
          {runsetups.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h5 style={{ color: '#f18f3c', marginBottom: '10px', borderBottom: '1px solid #444', paddingBottom: '5px' }}>
                Available RunSetups ({runsetups.length})
              </h5>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                backgroundColor: '#2a2a2a',
                borderRadius: '3px',
                padding: '10px'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', color: '#ccc' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #444' }}>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#f18f3c' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#f18f3c' }}>ID</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#f18f3c' }}>Deployment Path</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#f18f3c' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runsetups.map((rs) => {
                        const id = rs._id_norm;
                        const apiId = rs._api_id;
                        const name = rs.name || 'Unnamed';
                        const deploymentPath = rs.deployment_path || 'No path';
                        const isSelected = runsetupId === id;
                      return (
                        <tr
                            key={id}
                          style={{
                            borderBottom: '1px solid #333',
                            backgroundColor: isSelected ? '#3a3a3a' : 'transparent'
                          }}
                        >
                          <td style={{ padding: '8px', verticalAlign: 'top' }}>
                            <strong style={{ color: isSelected ? '#f18f3c' : '#fff' }}>{name}</strong>
                          </td>
                          <td style={{ padding: '8px', verticalAlign: 'top', fontFamily: 'monospace', fontSize: '0.9em' }}>
                            {id}
                          </td>
                          <td style={{ padding: '8px', verticalAlign: 'top', fontSize: '0.9em' }}>
                            {deploymentPath}
                          </td>
                          <td style={{ padding: '8px', verticalAlign: 'top' }}>
                            <button
                              onClick={() => {
                                this.setState({ runsetupId: id }, () => {
                                  this.listFiles(true); // Show toast when user selects a RunSetup
                                });
                              }}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: isSelected ? '#555' : '#444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '0.85em',
                                marginRight: '5px'
                              }}
                            >
                              {isSelected ? 'Selected' : 'Select'}
                            </button>
                            <button
                              onClick={() => this.getRunSetupUrl()}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#17a2b8',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '0.85em'
                              }}
                              title="Get URL for this RunSetup"
                            >
                              URL
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Files List */}
          <div style={{ marginTop: runsetups.length > 0 ? '10px' : '0' }}>
            <h5 style={{ color: '#f18f3c', marginBottom: '10px', borderBottom: '1px solid #444', paddingBottom: '5px' }}>
              Docker Compose Files {runsetupId && runsetupId.trim() ? `for RunSetup: ${runsetupId}` : ''}
            </h5>
            <div ref={el => this.filesContainer = el} style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {files.length === 0 ? (
                <div style={{ padding: '15px', textAlign: 'center' }}>
                  {runsetupId && runsetupId.trim() ? (
                    <p style={{ color: '#ccc', marginBottom: '10px' }}>
                      No files uploaded for this RunSetup. Upload a Docker Compose file above.
                    </p>
                  ) : (
                    <div>
                      <p style={{ color: '#ccc', marginBottom: '10px' }}>
                        To view uploaded Docker Compose files:
                      </p>
                      <ol style={{ color: '#aaa', textAlign: 'left', display: 'inline-block', margin: '0 auto' }}>
                        <li>Select a RunSetup from the list above</li>
                        <li>Or enter a RunSetup ID manually in the field above</li>
                        <li>Or upload a file with a RunSetup ID</li>
                        <li>Click "Refresh Files" to load files</li>
                      </ol>
                    </div>
                  )}
                </div>
              ) : (
                files.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #333',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <strong style={{ color: '#f18f3c' }}>File:</strong> {file.filename || file.name || 'Unknown'} &nbsp; | &nbsp;
                      <strong style={{ color: '#f18f3c' }}>Path:</strong> {file.file_path || file.path || 'N/A'}
                    </div>
                    <div style={{ fontSize: '0.9em', color: '#aaa' }}>
                      {file.size ? `Size: ${file.size}` : ''}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DockerComposePanel;